import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, FileText, DollarSign, ChevronDown, 
  RefreshCw, ArrowUp, ArrowDown, Clock, User, Filter
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getAuditLog } from '../services/api';
import { toast } from 'sonner';

const AdminAuditPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAuditLog({ search: searchTerm || undefined, action_type: actionFilter || undefined });
      setEntries(res.data);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, actionFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSearch = () => fetchLog();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  return (
    <AdminLayout>
      <div data-testid="admin-audit-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">Audit Log</h1>
          <p className="text-slate-400 mt-1">Track all admin balance adjustments and actions</p>
        </div>

        {/* Search & Filters */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
              placeholder="Search by user, email, admin, or description..."
              data-testid="audit-search"
            />
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 pr-10 text-sm text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/50 transition-all"
                data-testid="audit-filter"
              >
                <option value="">All Actions</option>
                <option value="balance_adjustment">Balance Adjustment</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
            <button onClick={handleSearch} className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-xl px-5 py-3 text-sm font-medium hover:bg-cyan-500/20 transition-all flex items-center gap-2" data-testid="audit-search-btn">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>
            <button onClick={() => { setSearchTerm(''); setActionFilter(''); }} className="bg-slate-800/50 text-slate-400 border border-white/10 rounded-xl px-4 py-3 text-sm hover:text-white transition-all" data-testid="audit-reset">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Audit Log Table — Desktop */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="hidden md:block rounded-xl border border-white/[0.06] bg-slate-900/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Date</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">User</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Action</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Amount</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Prev Balance</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">New Balance</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Reason</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-4">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan="8" className="px-5 py-4"><div className="h-10 bg-slate-800/50 rounded-lg animate-pulse" /></td></tr>
                  ))
                ) : entries.length > 0 ? (
                  entries.map((entry) => (
                    <tr key={entry.id} data-testid={`audit-row-${entry.id}`} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-slate-400 text-sm whitespace-nowrap">{formatDate(entry.created_at)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-white text-sm font-medium truncate max-w-[140px]">{entry.user_name || 'N/A'}</p>
                        <p className="text-slate-500 text-xs truncate max-w-[140px]">{entry.user_email}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          entry.operation === 'add'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : 'bg-red-500/15 text-red-400 border-red-500/30'
                        }`}>
                          {entry.operation === 'add' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                          {entry.operation === 'add' ? 'Add' : 'Subtract'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <p className={`text-sm font-semibold ${entry.operation === 'add' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {entry.operation === 'add' ? '+' : '-'}{formatCurrency(entry.amount)}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <p className="text-slate-400 text-sm">{formatCurrency(entry.previous_balance)}</p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <p className="text-white text-sm font-medium">{formatCurrency(entry.new_balance)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-400 text-sm truncate max-w-[200px]">{entry.description || 'No reason'}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-400 text-sm">{entry.admin_name}</p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-16">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-700" />
                      <p className="text-slate-500 font-medium">No audit log entries</p>
                      <p className="text-slate-600 text-sm mt-1">Balance adjustments will appear here</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && entries.length > 0 && (
            <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
              <p className="text-xs text-slate-500">Showing {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}</p>
            </div>
          )}
        </motion.div>

        {/* Audit Log Cards — Mobile */}
        <div className="md:hidden space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4">
                <div className="h-24 bg-slate-800/50 rounded-lg animate-pulse" />
              </div>
            ))
          ) : entries.length > 0 ? (
            entries.map((entry) => (
              <motion.div key={entry.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold text-sm truncate">{entry.user_name || 'N/A'}</p>
                    <p className="text-slate-500 text-xs truncate">{entry.user_email}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    entry.operation === 'add'
                      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                      : 'bg-red-500/15 text-red-400 border-red-500/30'
                  }`}>
                    {entry.operation === 'add' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {entry.operation === 'add' ? '+' : '-'}{formatCurrency(entry.amount)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500 mb-0.5">Previous</p>
                    <p className="text-slate-300">{formatCurrency(entry.previous_balance)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-0.5">New Balance</p>
                    <p className="text-white font-medium">{formatCurrency(entry.new_balance)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs pt-2 border-t border-white/[0.04]">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <User className="w-3 h-3" />
                    {entry.admin_name}
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Clock className="w-3 h-3" />
                    {formatDate(entry.created_at)}
                  </div>
                </div>
                {entry.description && (
                  <p className="text-xs text-slate-400 italic">"{entry.description}"</p>
                )}
              </motion.div>
            ))
          ) : (
            <div className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-700" />
              <p className="text-slate-500">No audit log entries</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditPage;
